/*
 * Free gift progress bar — <gift-bar>
 *
 * The snippet renders the bar server-side, so the first paint is already correct and
 * this file never has to draw it from nothing. What it does:
 *
 *   1. Keeps the bar in sync. Most cart interactions re-render the section the bar lives
 *      in, which replaces the element wholesale. Progress is remembered at module level
 *      so the replacement animates on from where the old one stopped instead of snapping
 *      back to zero.
 *   2. Adds the gift when the button is pressed.
 *   3. Removes gifts the cart no longer qualifies for.
 *
 * There can be several <gift-bar> elements alive at once — the cart page, the drawer and
 * the cart notification each render one. They share the module state below so they agree
 * on what has already been animated and celebrated, and so only one of them writes to the
 * cart when they all reach the same conclusion at the same moment.
 *
 * CART_EVENTS are the theme events that trigger a re-read of the cart. If the bar does
 * not move after adding a product, find the event the theme dispatches and add it here.
 */

(function () {
  if (customElements.get('gift-bar')) return;

  const CART_EVENTS = ['cart-update', 'cart:refresh', 'cart:updated'];

  /*
   * Bundled section rendering targets, keyed by the names in data-sections. Names with no
   * matching element on the page are dropped, so a section that does not exist on this
   * page — or a name that is simply wrong — fails quietly instead of breaking the request.
   */
  const SECTION_TARGETS = {
    'main-cart-items': { elementId: 'main-cart-items', selector: '.js-contents', dynamicId: true },
    'main-cart-footer': { elementId: 'main-cart-footer', selector: '.js-contents', dynamicId: true },
    'cart-drawer': { elementId: 'CartDrawer', selector: '.drawer__inner', sectionId: 'cart-drawer' },
    'cart-icon-bubble': { elementId: 'cart-icon-bubble', selector: '.shopify-section', sectionId: 'cart-icon-bubble' },
    'cart-live-region-text': {
      elementId: 'cart-live-region-text',
      selector: '.shopify-section',
      sectionId: 'cart-live-region-text',
    },
  };

  // Survives the section re-renders that destroy and recreate every <gift-bar>.
  const memory = { progress: null, reached: null, claimed: null };

  /*
   * Per-variant count of writes the cart did not honour. This is the loop guard, and it
   * matters more than it looks: /cart/update.js answers 200 with a silently clamped
   * quantity when it cannot do what was asked, so `response.ok` is not evidence of
   * anything. Every write is verified against the cart that comes back, a match clears the
   * tally and a mismatch increments it. At MAX_WRITE_ATTEMPTS the variant is left alone and
   * the claim button is offered instead.
   */
  const writeFailures = new Map();
  const MAX_WRITE_ATTEMPTS = 2;

  // The theme's own cart writes debounce at 300ms and coordinate with nobody, so settle
  // after them rather than racing them.
  const RECONCILE_DELAY = 400;

  let writing = false;
  let reconcileTimer = null;

  const themeRoutes = window.routes || {};
  const cartUrl = themeRoutes.cart_url || '/cart';
  const cartUpdateUrl = themeRoutes.cart_update_url || '/cart/update';

  /*
   * Every bar on the page reacts to the same cart event in the same tick. Sharing the
   * in-flight request means one round trip per update rather than one per bar.
   */
  let inflightCart = null;

  function fetchCart() {
    if (!inflightCart) {
      inflightCart = fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .finally(() => {
          inflightCart = null;
        });
    }
    return inflightCart;
  }

  /*
   * Both adding and removing a gift go through /cart/update.js rather than /cart/add.js.
   * It sets an exact quantity instead of incrementing, so a double click cannot land two
   * gifts, and it answers with the whole cart, which is what we need to repaint from.
   */
  function writeCart(updates, element) {
    return fetch(`${cartUpdateUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: sectionsToRequest(element),
        sections_url: window.location.pathname,
      }),
    }).then(async (response) => {
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'Cart update failed');
      return cart;
    });
  }

  /* ------------------------------------------------------------------ state -- */

  /*
   * Mirrors the Liquid in free-gift-bar.liquid. If you change the rules in one place,
   * change them in the other, or the bar will flicker between two answers on update.
   */
  function computeState(cart, config) {
    const tiers = config.tiers.slice().sort((a, b) => a.cents - b.cents);
    const giftIds = new Set(tiers.map((tier) => tier.variantId));

    // Quantity, not just presence — a gift the shopper managed to duplicate has to be
    // visible here so reconcile() can clamp it back to one.
    const inCart = new Map();

    let qualifyingTotal = 0;
    (cart.items || []).forEach((item) => {
      if (giftIds.has(item.variant_id)) {
        inCart.set(item.variant_id, (inCart.get(item.variant_id) || 0) + item.quantity);
        return;
      }
      qualifyingTotal += item.final_line_price;
    });

    const maxCents = tiers.length ? tiers[tiers.length - 1].cents : 0;
    const reached = tiers.filter((tier) => tier.cents <= qualifyingTotal);
    const next = tiers.find((tier) => tier.cents > qualifyingTotal) || null;

    // Without stacking only the highest tier reached is a target; the rest get swapped out.
    const targets = config.stackTiers ? reached : reached.slice(-1);
    const targetIds = new Set(targets.map((tier) => tier.variantId));

    return {
      tiers,
      inCart,
      targets,
      qualifyingTotal,
      progress: maxCents > 0 ? Math.min((qualifyingTotal / maxCents) * 100, 100) : 0,
      next,
      reached,
      pending: targets.filter((tier) => !inCart.has(tier.variantId)),
      claimed: targets.filter((tier) => inCart.has(tier.variantId)),
      stale: tiers.filter((tier) => inCart.has(tier.variantId) && !targetIds.has(tier.variantId)),
      state: targets.some((tier) => !inCart.has(tier.variantId)) ? 'unlocked' : next ? 'locked' : 'top',
      isEmpty: (cart.items || []).length === 0,
    };
  }

  /*
   * The gift lines the cart should be holding, as variantId -> quantity, limited to what
   * actually differs from the cart it was given. One target of each gift, nothing else.
   *
   * This is what replaced the separate add and remove paths: adding a newly earned gift,
   * swapping a smaller gift for a bigger one, dropping a stale one and clamping a
   * duplicated one are all the same diff, and all go out in one write.
   */
  function desiredUpdates(state) {
    const wanted = new Map();
    state.targets.forEach((tier) => wanted.set(tier.variantId, 1));
    state.inCart.forEach((quantity, variantId) => {
      if (!wanted.has(variantId)) wanted.set(variantId, 0);
    });

    const updates = {};
    wanted.forEach((quantity, variantId) => {
      if ((state.inCart.get(variantId) || 0) === quantity) return;
      if ((writeFailures.get(variantId) || 0) >= MAX_WRITE_ATTEMPTS) return;
      updates[variantId] = quantity;
    });
    return updates;
  }

  function formatMoney(cents, isoCode) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: isoCode,
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  /*
   * Placeholders are [amount] and [gift]. The Liquid uses the same two tokens — see the
   * note at the top of the snippet for why they are not {{ }}.
   */
  function renderMessage(template, values) {
    return String(template || '')
      .split('[amount]')
      .join(values.amount || '')
      .split('[gift]')
      .join(values.gift || '');
  }

  /* ------------------------------------------------------------ celebration -- */

  /*
   * Returns true the first time it is told about a cart that has crossed a threshold or
   * gained a gift. Every bar reports the same numbers, so only the first call through
   * wins and the page gets one burst of confetti rather than three.
   */
  function noteMilestones(reached, claimed) {
    const crossed = memory.reached !== null && reached > memory.reached;
    const gained = memory.claimed !== null && claimed > memory.claimed;
    memory.reached = reached;
    memory.claimed = claimed;
    return crossed || gained;
  }

  // Only a bar the shopper can actually see is worth throwing confetti at.
  function celebrateVisible() {
    document.querySelectorAll('gift-bar').forEach((bar) => {
      if (bar.isConnected && bar.offsetParent !== null && typeof bar.celebrate === 'function') bar.celebrate();
    });
  }

  /* ---------------------------------------------------------------- element -- */

  class GiftBar extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      if (!this.config) return;

      this.fill = this.querySelector('[data-gift-bar-fill]');
      this.track = this.querySelector('[data-gift-bar-track]');
      this.messageEl = this.querySelector('[data-gift-bar-message]');
      this.subEl = this.querySelector('[data-gift-bar-sub]');
      this.subTextEl = this.querySelector('[data-gift-bar-sub-text]');
      this.claimButton = this.querySelector('[data-gift-bar-claim]');
      this.claimLabel = this.querySelector('[data-gift-bar-claim-label]');
      this.errorEl = this.querySelector('[data-gift-bar-error]');
      this.confettiEl = this.querySelector('[data-gift-bar-confetti]');

      this.onClaim = this.onClaim.bind(this);
      if (this.claimButton) this.claimButton.addEventListener('click', this.onClaim);

      this.animateIn();
      this.listen();

      // Liquid has already told us whether anything is out of step — a gift to add, or one
      // that has outstayed its threshold — so the common case of a settled cart costs no
      // request at all.
      if (this.dataset.reconcile === 'true') scheduleReconcile(this, null);
    }

    disconnectedCallback() {
      if (this.claimButton) this.claimButton.removeEventListener('click', this.onClaim);
      (this.unsubscribers || []).forEach((off) => off());
      this.unsubscribers = [];
    }

    readConfig() {
      const script = this.querySelector('[data-gift-bar-config]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('[gift-bar] could not parse config', error);
        return null;
      }
    }

    listen() {
      this.unsubscribers = [];

      CART_EVENTS.forEach((eventName) => {
        if (typeof subscribe === 'function') {
          this.unsubscribers.push(
            subscribe(eventName, (payload) => {
              // Our own writes have already repainted every bar on the page.
              if (payload && payload.giftBar) return;
              this.refresh();
            })
          );
        }

        const handler = () => this.refresh();
        document.addEventListener(eventName, handler);
        this.unsubscribers.push(() => document.removeEventListener(eventName, handler));
      });
    }

    /*
     * Pick up where the last instance left off. Without this, a section re-render would
     * paint the bar at its new width instantly and the progress would never look like it
     * moved — which is the whole point of the thing.
     */
    animateIn() {
      const target = parseFloat(this.dataset.progress) || 0;

      if (this.fill && memory.progress !== null && Math.abs(memory.progress - target) > 0.01) {
        this.fill.style.width = `${memory.progress}%`;
        // Force the browser to accept the starting width before we change it again,
        // otherwise both writes collapse into one frame and nothing transitions.
        void this.fill.offsetWidth;
        requestAnimationFrame(() => {
          if (this.isConnected && this.fill) this.fill.style.width = `${target}%`;
        });
      }
      memory.progress = target;

      const milestone = noteMilestones(
        parseInt(this.dataset.unlocked, 10) || 0,
        parseInt(this.dataset.claimed, 10) || 0
      );
      // Give the other bars on the page a frame to upgrade before we look for a visible one.
      if (milestone) requestAnimationFrame(celebrateVisible);
    }

    refresh() {
      return fetchCart()
        .then((cart) => {
          if (!this.isConnected) return;
          repaintAll(cart);
          if (Object.keys(desiredUpdates(computeState(cart, this.config))).length > 0) {
            scheduleReconcile(this, cart);
          }
        })
        .catch((error) => console.error('[gift-bar]', error));
    }

    render(state) {
      // An empty cart has nothing to measure, so the bar has nothing to say. Reveal
      // before the width changes below, or the first item slides the fill while the
      // element is still display:none and it lands with no animation at all.
      this.hidden = state.isEmpty;

      const { strings, isoCode } = this.config;
      const gap = state.next ? formatMoney(state.next.cents - state.qualifyingTotal, isoCode) : '';

      let message;
      if (state.state === 'unlocked') {
        message = renderMessage(strings.unlocked, { gift: state.pending[state.pending.length - 1].title, amount: gap });
      } else if (state.state === 'locked') {
        message = renderMessage(strings.locked, { amount: gap, gift: state.next.title });
      } else {
        const topGift = state.reached.length ? state.reached[state.reached.length - 1].title : '';
        message = renderMessage(strings.top, { gift: topGift, amount: gap });
      }

      if (this.messageEl) this.messageEl.textContent = message;

      const claimedTitle = state.claimed.length ? state.claimed[state.claimed.length - 1].title : '';
      const showSub = Boolean(claimedTitle) && state.state !== 'top';
      if (this.subEl) {
        this.subEl.hidden = !showSub;
        if (showSub && this.subTextEl) {
          this.subTextEl.textContent = renderMessage(strings.claimed, { gift: claimedTitle });
        }
      }

      if (this.fill) this.fill.style.width = `${state.progress}%`;
      if (this.track) {
        this.track.setAttribute('aria-valuenow', Math.round(state.progress));
        this.track.setAttribute('aria-label', message);
      }

      this.querySelectorAll('[data-gift-bar-tier]').forEach((node) => {
        const cents = parseInt(node.dataset.giftBarTier, 10);
        const tier = state.tiers.find((candidate) => candidate.cents === cents);
        let tierState = 'locked';
        if (tier && cents <= state.qualifyingTotal) {
          tierState = state.inCart.has(tier.variantId) ? 'claimed' : 'unlocked';
        }
        node.classList.remove('gift-bar__tier--locked', 'gift-bar__tier--unlocked', 'gift-bar__tier--claimed');
        node.classList.add(`gift-bar__tier--${tierState}`);
      });

      // The gift is added automatically, so the button is a fallback, not the normal path.
      // It appears only once the cart has refused the write as many times as we are
      // willing to try — otherwise it would flash on every paint before the add lands.
      const needsManualClaim = hasExhaustedTarget(state);
      if (this.claimButton) {
        this.claimButton.hidden = !needsManualClaim;
        if (needsManualClaim && this.claimLabel) {
          this.claimLabel.textContent = state.pending[state.pending.length - 1].buttonLabel;
        }
      }
      this.classList.toggle('is-adding', state.pending.length > 0 && !needsManualClaim);

      this.dataset.state = state.state;
      this.dataset.progress = state.progress;
      this.dataset.unlocked = state.reached.length;
      this.dataset.claimed = state.claimed.length;
      memory.progress = state.progress;
    }

    /*
     * Only reachable once automatic adding has given up, so this is a deliberate retry by
     * the shopper. Clearing the failure tally is the whole point — it re-opens the write
     * that desiredUpdates() is otherwise refusing to make. If it fails again the tally
     * refills and the button comes back.
     */
    onClaim(event) {
      event.preventDefault();
      if (writing || this.classList.contains('is-loading')) return;

      this.setLoading(true);
      this.setError('');
      writeFailures.clear();

      reconcile(this, null)
        .then(() => {
          if (!this.isConnected) return;
          if (writeFailures.size > 0) this.setError(this.config.strings.error);
          else this.celebrate();
        })
        .finally(() => this.setLoading(false));
    }

    setLoading(isLoading) {
      if (!this.isConnected) return;
      this.classList.toggle('is-loading', isLoading);
      if (this.claimButton) this.claimButton.disabled = isLoading;
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    celebrate() {
      if (!this.confettiEl) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const colors = ['#ffd166', '#ef476f', '#06d6a0', '#4cc9f0', '#f77f00', '#b5179e'];
      const pieces = document.createDocumentFragment();

      for (let index = 0; index < 28; index += 1) {
        const piece = document.createElement('i');
        piece.style.setProperty('--gift-bar-confetti-left', `${10 + Math.random() * 80}%`);
        piece.style.setProperty('--gift-bar-confetti-top', `${20 + Math.random() * 30}%`);
        piece.style.setProperty('--gift-bar-confetti-x', `${(Math.random() - 0.5) * 18}rem`);
        piece.style.setProperty('--gift-bar-confetti-y', `${4 + Math.random() * 8}rem`);
        piece.style.setProperty('--gift-bar-confetti-rotate', `${(Math.random() - 0.5) * 900}deg`);
        piece.style.setProperty('--gift-bar-confetti-color', colors[index % colors.length]);
        piece.style.setProperty('--gift-bar-confetti-delay', `${Math.random() * 180}ms`);
        pieces.appendChild(piece);
      }

      this.confettiEl.replaceChildren(pieces);
      window.setTimeout(() => {
        if (this.confettiEl) this.confettiEl.replaceChildren();
      }, 1500);
    }
  }

  /* --------------------------------------------------------------- repaint -- */

  function repaintAll(cart) {
    const bars = Array.from(document.querySelectorAll('gift-bar')).filter((bar) => bar.isConnected && bar.config);
    if (bars.length === 0) return;

    let last = null;
    bars.forEach((bar) => {
      last = computeState(cart, bar.config);
      bar.render(last);
    });

    if (last && noteMilestones(last.reached.length, last.claimed.length)) celebrateVisible();
  }

  /* -------------------------------------------------------------- sections -- */

  function sectionsToRequest(element) {
    const names = element && element.dataset ? element.dataset.sections || '' : '';
    return names
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean)
      .map(resolveSection)
      .filter(Boolean)
      .map((target) => target.sectionId);
  }

  function resolveSection(name) {
    const target = SECTION_TARGETS[name];
    if (!target) return null;

    const element = document.getElementById(target.elementId);
    if (!element) return null;

    const sectionId = target.dynamicId ? element.dataset.id : target.sectionId;
    if (!sectionId) return null;

    return Object.assign({}, target, { element, sectionId });
  }

  function applySections(sections) {
    if (!sections) return;

    Object.keys(SECTION_TARGETS).forEach((name) => {
      const target = resolveSection(name);
      if (!target) return;

      const html = sections[target.sectionId];
      if (!html) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      if (!parsed) return;

      const destination = target.element.querySelector(target.selector) || target.element;
      destination.innerHTML = parsed.innerHTML;
    });
  }

  /*
   * We have already repainted the cart sections, so cart-items and cart-drawer-items
   * would only refetch what is on screen. They skip events tagged 'cart-items' for
   * exactly that reason, while every other subscriber still hears it. The giftBar flag
   * keeps the bars themselves from re-reading a cart they just wrote.
   */
  function announceCartUpdate(cartData) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', giftBar: true, cartData });
  }

  /* ------------------------------------------------------------- reconcile -- */

  /*
   * Bring the cart's gift lines in line with what it has earned: add a newly unlocked
   * gift, swap a smaller one for a bigger one, drop a stale one, clamp a duplicated one.
   * One diff, one write.
   *
   * Debounced, because the theme's own cart writes are debounced at 300ms and nothing
   * coordinates the two. Firing immediately means reconciling against a cart that is about
   * to change again.
   */
  function scheduleReconcile(element, cart) {
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(() => reconcile(element, cart), RECONCILE_DELAY);
  }

  function reconcile(element, cart) {
    if (writing || !element.isConnected || !element.config) return Promise.resolve();

    // Re-read rather than trusting the cart we were handed — it was fetched before the
    // debounce window, and the theme may have written to it since.
    return fetchCart()
      .then((fresh) => {
        const updates = desiredUpdates(computeState(fresh, element.config));
        if (Object.keys(updates).length === 0) return null;

        writing = true;
        return writeCart(updates, element).then((written) => {
          verifyWrite(updates, written);
          applySections(written.sections);
          repaintAll(written);
          announceCartUpdate(written);
          return written;
        });
      })
      .catch((error) => {
        // A rejected request is a failed attempt for everything it was carrying.
        console.error('[gift-bar]', error);
      })
      .finally(() => {
        writing = false;
      });
  }

  /*
   * The whole reason this function exists: a 200 from /cart/update.js does not mean the
   * cart did what was asked. Compare quantities against the cart that came back.
   */
  function verifyWrite(updates, cart) {
    const actual = new Map();
    (cart.items || []).forEach((item) => {
      actual.set(item.variant_id, (actual.get(item.variant_id) || 0) + item.quantity);
    });

    Object.keys(updates).forEach((key) => {
      const variantId = Number(key);
      if ((actual.get(variantId) || 0) === updates[key]) {
        writeFailures.delete(variantId);
      } else {
        writeFailures.set(variantId, (writeFailures.get(variantId) || 0) + 1);
      }
    });
  }

  // A gift we have given up trying to add is the only reason to show the claim button.
  function hasExhaustedTarget(state) {
    return state.pending.some((tier) => (writeFailures.get(tier.variantId) || 0) >= MAX_WRITE_ATTEMPTS);
  }

  customElements.define('gift-bar', GiftBar);
})();
